import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiChevronDown, FiChevronUp, FiZoomIn } from 'react-icons/fi';
import { FaYoutube, FaWhatsapp } from 'react-icons/fa';
import { SiX } from 'react-icons/si';
import { AiOutlineInstagram } from 'react-icons/ai';
import { BsFacebook } from 'react-icons/bs';
import { FaTape } from 'react-icons/fa6';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import api from '../../services/api';
import GalleryModal from '../common/GalleryModal';
import SizeChartModal from '../common/SizeChartModal';

const QuickView = ({ product, onClose, onAddToCart }) => {
    const { theme } = useTheme();
    const [settings, setSettings] = useState({
        saleBoxEnabled: true,
        saleBoxContent: '⚠️ LAST WEEK OF SALE ⚠️',
        saleBoxColor: '#dc2626'
    });
    const [socialLinks, setSocialLinks] = useState([]);
    const [showSizeChart, setShowSizeChart] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await api.get('/settings/announcement');
                setSettings({
                    saleBoxEnabled: res.saleBoxEnabled !== undefined ? res.saleBoxEnabled : true,
                    saleBoxContent: res.saleBoxContent || '⚠️ LAST WEEK OF SALE ⚠️',
                    saleBoxColor: res.saleBoxColor || '#dc2626'
                });
            } catch (err) {
                console.error('Failed to fetch settings');
            }
        };
        fetchSettings();
    }, []);

    useEffect(() => {
        const fetchSocialLinks = async () => {
            try {
                const footer = await api.get('/footer');
                if (footer.socialLinks) {
                    setSocialLinks(footer.socialLinks);
                }
            } catch (err) {
                console.error('Failed to fetch social links', err);
            }
        };
        fetchSocialLinks();
    }, []);

    const variants = product.variants || [];
    const sizes = [...new Set(variants.map(v => v.size).filter(Boolean))];
    const colors = [...new Set(variants.map(v => v.color).filter(Boolean))];
    
    const [selectedSize, setSelectedSize] = useState('');
    const [selectedColor, setSelectedColor] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [activeImage, setActiveImage] = useState(0);
    const [openAccordion, setOpenAccordion] = useState(null);
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [galleryIndex, setGalleryIndex] = useState(0);
    
    const imageScrollRef = useRef(null);
    const contentScrollRef = useRef(null);

    useEffect(() => {
        if (colors.length > 0 && !selectedColor) {
            setSelectedColor(colors[0]);
        }
        if (sizes.length > 0 && !selectedSize) {
            setSelectedSize(sizes[0]);
        }
    }, [product]);

    if (!product) return null;

    const fixedImageUrl = product.images && product.images.length > 0
        ? product.images[0]
        : (product.image || 'https://stylo.pk/cdn/shop/files/CL563801_1.png?v=1773727069&width=1080');
        
    const thumbnails = product.thumbnails && product.thumbnails.length
        ? product.thumbnails
        : (product.images && product.images.length > 0 ? product.images : [fixedImageUrl, fixedImageUrl, fixedImageUrl]);

    const toggleAccordion = (section) => {
        setOpenAccordion(openAccordion === section ? null : section);
    };

    const handleBuyNow = () => {
        const variant = variants.find(v => v.size === selectedSize && v.color === selectedColor);
        if (!variant || !variant.inStock || variant.quantity <= 0) {
            alert('Selected variant is out of stock');
            return;
        }
        if (quantity > variant.quantity) {
            alert(`Only ${variant.quantity} items available in stock`);
            setQuantity(variant.quantity);
            return;
        }
        onAddToCart({ ...product, quantity, selectedSize, selectedColor });
        window.location.href = '/checkout';
    };

    const selectedVariant = variants.find(v => v.size === selectedSize && v.color === selectedColor);
    const isVariantInStock = selectedVariant ? (selectedVariant.inStock && selectedVariant.quantity > 0) : false;
    const isProductCompletelyOutOfStock = variants.every(v => !v.inStock || v.quantity <= 0);
    const isSizeInStock = (size) => variants.some(v => v.size === size && v.inStock && v.quantity > 0);
    const isColorInStock = (color) => {
        if (!selectedSize) return variants.some(v => v.color === color && v.inStock && v.quantity > 0);
        return variants.some(v => v.color === color && v.size === selectedSize && v.inStock && v.quantity > 0);
    };

    useEffect(() => {
        const contentEl = contentScrollRef.current;
        const imageEl = imageScrollRef.current;
        if (!contentEl || !imageEl) return;

        const handleWheel = (e) => {
            const delta = e.deltaY;
            const isScrollingDown = delta > 0;
            const isScrollingUp = delta < 0;

            const contentScrollTop = contentEl.scrollTop;
            const contentScrollHeight = contentEl.scrollHeight;
            const contentClientHeight = contentEl.clientHeight;
            const isContentAtBottom = contentScrollTop + contentClientHeight >= contentScrollHeight - 1;
            const isContentAtTop = contentScrollTop <= 0;

            const imageScrollTop = imageEl.scrollTop;
            const imageScrollHeight = imageEl.scrollHeight;
            const imageClientHeight = imageEl.clientHeight;
            const isImageAtBottom = imageScrollTop + imageClientHeight >= imageScrollHeight - 1;
            const isImageAtTop = imageScrollTop <= 0;

            if (isScrollingDown) {
                if (!isContentAtBottom) {
                    contentEl.scrollTop += delta;
                    e.preventDefault();
                } else if (!isImageAtBottom) {
                    imageEl.scrollTop += delta;
                    e.preventDefault();
                }
            } else if (isScrollingUp) {
                if (!isImageAtTop) {
                    imageEl.scrollTop += delta;
                    e.preventDefault();
                } else if (!isContentAtTop) {
                    contentEl.scrollTop += delta;
                    e.preventDefault();
                }
            }
        };

        const modal = contentEl.closest('.quick-view-modal');
        if (modal) {
            modal.addEventListener('wheel', handleWheel, { passive: false });
            return () => modal.removeEventListener('wheel', handleWheel);
        }
    }, []);

    const openGallery = (index) => {
        setGalleryIndex(index);
        setIsGalleryOpen(true);
    };

    const closeGallery = () => setIsGalleryOpen(false);
    const nextGalleryImage = () => setGalleryIndex((prev) => (prev + 1) % thumbnails.length);
    const prevGalleryImage = () => setGalleryIndex((prev) => (prev - 1 + thumbnails.length) % thumbnails.length);

    const primaryColor = theme.primaryColor;
    const secondaryColor = theme.secondaryColor;
    const textColor = theme.textColor;

    const getSocialLink = (iconName) => {
        const link = socialLinks.find(s => s.icon === iconName);
        return link ? link.link : '#';
    };

    const modalContent = (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 99999,
                padding: '20px'
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div
                className="quick-view-modal"
                style={{
                    width: '95%',
                    maxWidth: '1400px',
                    height: '90vh',
                    background: '#fff',
                    borderRadius: '0',
                    overflow: 'hidden',
                    position: 'relative',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
                }}
            >
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '20px',
                        right: '20px',
                        width: '45px',
                        height: '45px',
                        borderRadius: '50%',
                        border: 'none',
                        background: '#888',
                        color: 'white',
                        cursor: 'pointer',
                        zIndex: 20,
                        fontSize: '22px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <FiX />
                </button>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '120px 1fr 1fr',
                        gap: '10px',
                        height: '100%',
                        overflow: 'hidden',
                        padding: '0 50px'
                    }}
                >
                    <div
                        style={{
                            padding: '30px 10px',
                            overflowY: 'auto',
                            borderRight: '1px solid #f3f3f3',
                            scrollbarWidth: 'thin'
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '15px',
                                alignItems: 'center'
                            }}
                        >
                            {thumbnails.map((thumb, idx) => (
                                <img
                                    key={idx}
                                    src={thumb}
                                    alt=""
                                    onClick={() => setActiveImage(idx)}
                                    style={{
                                        width: '90px',
                                        height: '120px',
                                        objectFit: 'cover',
                                        cursor: 'pointer',
                                        border: idx === activeImage ? `2px solid ${primaryColor}` : '2px solid #eee',
                                        padding: '2px'
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                    <div
                        ref={imageScrollRef}
                        style={{
                            overflowY: 'auto',
                            height: '100%',
                            padding: '30px 40px',
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                            position: 'relative'
                        }}
                        className="hide-scrollbar"
                    >
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'flex-start',
                                minHeight: '100%'
                            }}
                        >
                            <img
                                src={thumbnails[activeImage] || fixedImageUrl}
                                alt="Product"
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    maxHeight: '80vh',
                                    display: 'block',
                                    objectFit: 'contain'
                                }}
                            />
                        </div>
                        <button
                            onClick={() => openGallery(activeImage)}
                            style={{
                                position: 'absolute',
                                bottom: '20px',
                                right: '20px',
                                background: 'rgba(255,255,255,0.8)',
                                border: 'none',
                                borderRadius: '50%',
                                width: '44px',
                                height: '44px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'white'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.8)'}
                        >
                            <FiZoomIn size={20} />
                        </button>
                    </div>
                    <div
                        ref={contentScrollRef}
                        style={{
                            padding: '30px 40px 50px 20px',
                            overflowY: 'auto',
                            height: '100%',
                            scrollbarWidth: 'thin'
                        }}
                    >
                        <h2 style={{ fontSize: '31px', fontWeight: 400, marginBottom: '15px', fontFamily: 'inherit', color: textColor }}>
                            {product.name}
                        </h2>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap', marginBottom: '24px' }}>
                            {product.originalPrice && (
                                <span style={{ textDecoration: 'line-through', color: 'var(--muted-text)', fontSize: '18px', fontWeight: 600 }}>
                                    Rs. {product.originalPrice.toLocaleString()}
                                </span>
                            )}
                            <span style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, fontSize: '18px', color: textColor, lineHeight: '25px' }}>
                                Rs. {product.price.toLocaleString()}
                            </span>
                            {product.discount && (
                                <span style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400, fontSize: '16px', color: primaryColor, lineHeight: '22px' }}>
                                    Save {product.discount}%
                                </span>
                            )}
                        </div>
                        {isProductCompletelyOutOfStock && (
                            <div style={{
                                backgroundColor: '#ef4444',
                                border: `1px solid #ef4444`,
                                borderRadius: '4px',
                                padding: '16px 12px 40px 12px',
                                textAlign: 'center',
                                marginBottom: '24px'
                            }}>
                                <div style={{ color: 'white', fontSize: '20px', fontWeight: 700 }}>SOLD OUT</div>
                            </div>
                        )}
                        {settings.saleBoxEnabled && !isProductCompletelyOutOfStock && (
                            <div style={{
                                backgroundColor: settings.saleBoxColor,
                                border: `1px solid ${settings.saleBoxColor}`,
                                borderRadius: '4px',
                                padding: '16px 12px 40px 12px',
                                textAlign: 'center',
                                marginBottom: '24px'
                            }}>
                                <span style={{ color: 'white', fontSize: '16px', fontWeight: 600 }}>{settings.saleBoxContent}</span>
                                <p style={{ color: '#fef2f2', fontSize: '16px', fontWeight: 600 }}>Shop Before it Ends</p>
                            </div>
                        )}
                        {colors.length > 0 && (
                            <div style={{ marginBottom: '24px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                    <h4 style={{ fontSize: '16px', fontWeight: 400, color: textColor }}>Color:</h4>
                                    <span style={{ fontSize: '14px', color: textColor }}>{selectedColor || 'Select'}</span>
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                    {colors.map((color) => {
                                        const inStock = isColorInStock(color);
                                        return (
                                            <button
                                                key={color}
                                                onClick={() => inStock && setSelectedColor(color)}
                                                style={{
                                                    padding: '8px 12px',
                                                    border: selectedColor === color ? `1px solid ${primaryColor}` : '1px solid #ddd',
                                                    background: selectedColor === color ? primaryColor : 'white',
                                                    color: selectedColor === color ? 'white' : textColor,
                                                    fontSize: '16px',
                                                    fontWeight: 400,
                                                    cursor: inStock ? 'pointer' : 'not-allowed',
                                                    borderRadius: '2px',
                                                    opacity: inStock ? 1 : 0.5,
                                                    textDecoration: inStock ? 'none' : 'line-through'
                                                }}
                                                disabled={!inStock}
                                            >
                                                {color}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {sizes.length > 0 && (
                            <div style={{ marginBottom: '24px' }}>
                                <h4 style={{ fontSize: '16px', fontWeight: 400, color: textColor, marginBottom: '10px' }}>Size:</h4>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
                                    {sizes.map(size => {
                                        const inStock = isSizeInStock(size);
                                        return (
                                            <button
                                                key={size}
                                                onClick={() => inStock && setSelectedSize(size)}
                                                style={{
                                                    padding: '8px 12px',
                                                    border: selectedSize === size ? `1px solid ${primaryColor}` : '1px solid #ddd',
                                                    background: selectedSize === size ? primaryColor : 'white',
                                                    color: selectedSize === size ? 'white' : textColor,
                                                    fontSize: '16px',
                                                    fontWeight: 400,
                                                    cursor: inStock ? 'pointer' : 'not-allowed',
                                                    borderRadius: '2px',
                                                    opacity: inStock ? 1 : 0.5,
                                                    textDecoration: inStock ? 'none' : 'line-through'
                                                }}
                                                disabled={!inStock}
                                            >
                                                {size}
                                            </button>
                                        );
                                    })}
                                </div>
                                <button
                                    onClick={() => setShowSizeChart(true)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        cursor: 'pointer',
                                        marginBottom: '20px',
                                        textDecoration: 'none',
                                        color: textColor,
                                        background: 'none',
                                        border: 'none'
                                    }}
                                >
                                    <span style={{
                                        fontSize: '14px',
                                        letterSpacing: '2px',
                                        textTransform: 'uppercase',
                                        fontWeight: 300,
                                        color: textColor
                                    }}>
                                        SIZE CHART
                                    </span>
                                    <FaTape size={20} />
                                </button>
                            </div>
                        )}
                        <div style={{ marginBottom: '15px' }}>
                            <h4 style={{
                                fontSize: '14px',
                                letterSpacing: '2px',
                                textTransform: 'uppercase',
                                fontWeight: 300,
                                color: textColor,
                                marginBottom: '10px',
                                marginTop: 0
                            }}>
                                QUANTITY
                            </h4>
                            <div style={{
                                display: 'inline-flex',
                                border: '1px solid #ddd',
                                borderRadius: '2px',
                                alignItems: 'center',
                                overflow: 'hidden'
                            }}>
                                <button
                                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                                    style={{
                                        width: '44px',
                                        height: '44px',
                                        background: 'white',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '24px',
                                        fontWeight: 300,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: textColor
                                    }}
                                >
                                    −
                                </button>
                                <span style={{
                                    width: '48px',
                                    textAlign: 'center',
                                    lineHeight: '44px',
                                    fontSize: '16px',
                                    fontWeight: 400,
                                    borderLeft: '1px solid #ddd',
                                    borderRight: '1px solid #ddd',
                                    color: textColor
                                }}>
                                    {quantity}
                                </span>
                                <button
                                    onClick={() => setQuantity(q => (selectedVariant ? Math.min(selectedVariant.quantity, q + 1) : q + 1))}
                                    style={{
                                        width: '44px',
                                        height: '44px',
                                        background: 'white',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontSize: '24px',
                                        fontWeight: 300,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: textColor
                                    }}
                                >
                                    +
                                </button>
                            </div>
                            <p style={{ fontSize: '12px', color: 'var(--muted-text)', marginTop: '5px' }}>
                                Max available: {selectedVariant ? selectedVariant.quantity : 0}
                            </p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '5px' }}>
                            <span style={{
                                display: 'inline-block',
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                backgroundColor: isVariantInStock ? '#22c55e' : '#ef4444',
                            }}></span>
                            <span style={{ fontSize: '15px', color: textColor }}>
                                {isVariantInStock ? `In stock (${selectedVariant?.quantity || 0} available)` : 'Out of stock'}
                            </span>
                        </div>
                        <div style={{ marginBottom: '24px' }}>
                            <p style={{
                                fontFamily: 'Montserrat, sans-serif',
                                fontWeight: 400,
                                fontSize: '16px',
                                color: textColor,
                                lineHeight: '22px',
                                margin: 0
                            }}>
                                🚚 Get Free Delivery On Prepaid & Above Rs 3500 Orders. 100% Recycled Packaging & Eco-Friendly Deliveries
                            </p>
                        </div>
                        <div style={{ border: '1px solid #e5e5e5', borderRadius: '4px', marginBottom: '25px' }}>
                            <div style={{ borderBottom: '1px solid #e5e5e5', overflow: 'hidden' }}>
                                <div
                                    onClick={() => toggleAccordion('description')}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '16px 20px',
                                        cursor: 'pointer',
                                        background: '#fff'
                                    }}
                                >
                                    <span style={{ flex: 1, textAlign: 'center', fontSize: '14px', letterSpacing: '2px', textTransform: 'uppercase', color: textColor }}>
                                        DESCRIPTION
                                    </span>
                                    <div style={{ marginLeft: '10px' }}>
                                        {openAccordion === 'description' ? <FiChevronUp size={18} style={{ color: textColor }} /> : <FiChevronDown size={18} style={{ color: textColor }} />}
                                    </div>
                                </div>
                                <div
                                    style={{
                                        maxHeight: openAccordion === 'description' ? '500px' : '0px',
                                        opacity: openAccordion === 'description' ? 1 : 0,
                                        transition: 'max-height 0.4s cubic-bezier(0.25, 0.8, 0.25, 1), opacity 0.3s ease',
                                        overflow: 'hidden'
                                    }}
                                >
                                    <div style={{ padding: '15px 20px', fontSize: '14px', color: textColor, lineHeight: '1.5' }}>
                                        {product.description || "Elegant and comfortable heels perfect for any occasion."}
                                    </div>
                                </div>
                            </div>
                            <div style={{ overflow: 'hidden' }}>
                                <div
                                    onClick={() => toggleAccordion('shipping')}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '16px 20px',
                                        cursor: 'pointer',
                                        background: '#fff'
                                    }}
                                >
                                    <span style={{ flex: 1, textAlign: 'center', fontSize: '14px', letterSpacing: '2px', textTransform: 'uppercase', color: textColor }}>
                                        SHIPPING INFORMATION
                                    </span>
                                    <div style={{ marginLeft: '10px' }}>
                                        {openAccordion === 'shipping' ? <FiChevronUp size={18} style={{ color: textColor }} /> : <FiChevronDown size={18} style={{ color: textColor }} />}
                                    </div>
                                </div>
                                <div
                                    style={{
                                        maxHeight: openAccordion === 'shipping' ? '500px' : '0px',
                                        opacity: openAccordion === 'shipping' ? 1 : 0,
                                        transition: 'max-height 0.4s cubic-bezier(0.25, 0.8, 0.25, 1), opacity 0.3s ease',
                                        overflow: 'hidden'
                                    }}
                                >
                                    <div style={{ padding: '15px 20px', fontSize: '14px', color: textColor, lineHeight: '1.5' }}>
                                        <p>• Free shipping on orders above Rs. 3,500</p>
                                        <p>• Estimated delivery: 3-5 business days</p>
                                        <p>• Easy returns within 7 days</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div style={{ marginTop: '25px', marginBottom: '25px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <button
                                    onClick={() => {
                                        if (!isVariantInStock) {
                                            alert('Selected variant is out of stock');
                                            return;
                                        }
                                        if (quantity > selectedVariant.quantity) {
                                            alert(`Only ${selectedVariant.quantity} items available in stock`);
                                            setQuantity(selectedVariant.quantity);
                                            return;
                                        }
                                        onAddToCart({ ...product, quantity, selectedSize, selectedColor });
                                        onClose();
                                    }}
                                    disabled={!isVariantInStock || isProductCompletelyOutOfStock}
                                    style={{
                                        width: '100%',
                                        border: '2px solid black',
                                        background: 'white',
                                        color: 'black',
                                        padding: '14px',
                                        fontSize: '14px',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '1px',
                                        cursor: isVariantInStock ? 'pointer' : 'not-allowed'
                                    }}
                                >
                                    {isProductCompletelyOutOfStock ? 'SOLD OUT' : (isVariantInStock ? 'ADD TO CART' : 'OUT OF STOCK')}
                                </button>
                                <button
                                    onClick={handleBuyNow}
                                    disabled={!isVariantInStock || isProductCompletelyOutOfStock}
                                    style={{
                                        width: '100%',
                                        background: isVariantInStock ? primaryColor : '#ccc',
                                        color: 'white',
                                        border: 'none',
                                        padding: '14px',
                                        fontSize: '14px',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '1px',
                                        cursor: isVariantInStock ? 'pointer' : 'not-allowed'
                                    }}
                                >
                                    BUY IT NOW
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <GalleryModal
                isOpen={isGalleryOpen}
                images={thumbnails}
                currentIndex={galleryIndex}
                onClose={closeGallery}
                onPrev={prevGalleryImage}
                onNext={nextGalleryImage}
            />
            <SizeChartModal isOpen={showSizeChart} onClose={() => setShowSizeChart(false)} />
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default QuickView;